/** Shared fetch helper — injects auth token and tenant/org/brand/workspace headers. */
import { getScopeHeaders } from '../lib/auth-storage';

/**
 * Unwrap standard API envelope `{ data, meta? }`.
 *
 * IMPORTANT: `body.data ?? body` is wrong when `data` is JSON null — nullish
 * coalescing skips `null` and returns the whole envelope object, which then
 * crashes `.map()` (TypeError: a.map is not a function).
 */
export function unwrapEnvelopeData<T>(body: unknown): T {
  if (body !== null && typeof body === 'object' && Object.prototype.hasOwnProperty.call(body, 'data')) {
    return (body as { data: T }).data;
  }
  return body as T;
}

/** List endpoints may return JSON null (Go nil slice). Always coerce to an array. */
export function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

export async function fetchJSON<T>(
  base: string,
  pathOrInit?: string | RequestInit,
  init?: RequestInit,
): Promise<T> {
  let url: string;
  let options: RequestInit | undefined;
  if (typeof pathOrInit === 'string') {
    url = `${base}${pathOrInit}`;
    options = init;
  } else {
    url = base;
    options = pathOrInit;
  }

  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getScopeHeaders(),
      ...(options?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${url} failed: ${res.status} ${text}`);
  }
  const body = await res.json();
  return unwrapEnvelopeData<T>(body);
}
